import { useQuery } from '@apollo/client';
import { TextBox } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  setContactInfoPhoneInputValue,
  setCustomerLocationPhone,
  setCustomerPhone,
  setFirstName,
  setLastName,
} from 'actions';
import { CarouselContinueButton } from 'components/CarouselContinueButton';
import { Phrase } from 'components/Phrase';
import { ReactComponent as InputMobilePhoneSvg } from 'components/svg/InputMobilePhone.svg';
import { ReactComponent as InputPersonSvg } from 'components/svg/InputPerson.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { IState } from 'typings';
import {
  IGetCorrectedPhoneNumberQuery,
  IGetCorrectedPhoneNumberQueryVariables,
} from 'typings/GraphQL';
import getPhoneNumberDigits from 'utils/getPhoneNumberDigits';
import isMaskedPhoneInputValid from 'utils/isMaskedPhoneInputValid';

import { Buttons, Container, FormGroup, NoteContainer } from './styles';

const getCorrectedPhoneNumberQuery = loader(
  '../../queries/getCorrectedPhoneNumber.graphql',
);

export const CarouselItemContactInfoSalesBottom = () => {
  const dispatch = useThunkDispatch();
  const theme = useTheme();
  const {
    contactInfoPhoneInputValue,
    countryCode = 'US',
    currentCarouselItemId,
    firstName = '',
    lastName = '',
  } = useSelector((state: IState) => ({
    contactInfoPhoneInputValue: state.userInput.contactInfoPhoneInputValue,
    countryCode: state.client.countryCodes[0],
    currentCarouselItemId: state.serviceCarousel.currentItemId,
    firstName: state.userInput.firstName,
    lastName: state.userInput.lastName,
  }));

  const isCurrentCarouselItemVisible =
    currentCarouselItemId === CarouselItemId.ContactInfo;
  const isPhoneInputValid = !contactInfoPhoneInputValue
    ? null
    : isMaskedPhoneInputValid(contactInfoPhoneInputValue);

  const {
    data: getCorrectedPhoneNumberData,
    error: getCorrectedPhoneNumberError,
    loading: isLoading,
  } = useQuery<
    IGetCorrectedPhoneNumberQuery,
    IGetCorrectedPhoneNumberQueryVariables
  >(getCorrectedPhoneNumberQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const phone = data?.getCorrectedPhoneNumber?.phone;

      if (phone) {
        dispatch(setCustomerPhone(phone));
      }
    },
    skip: !isPhoneInputValid || !isCurrentCarouselItemVisible,
    variables: {
      countryCode,
      phoneNumber: contactInfoPhoneInputValue,
    },
  });

  const correctedPhoneNumber =
    getCorrectedPhoneNumberData?.getCorrectedPhoneNumber?.phone?.number || '';
  const isCorrectedPhoneNumberValid: boolean | undefined =
    getCorrectedPhoneNumberData?.getCorrectedPhoneNumber?.isValid;

  const isMobile =
    getCorrectedPhoneNumberData?.getCorrectedPhoneNumber?.phone?.isMobile;
  const isPhoneOkay =
    !isLoading && (isMobile === true || isPhoneInputValid === true);
  const isPhoneSuccessful =
    !isLoading && isMobile === true && isPhoneInputValid === true;
  const isFormValid = !!(
    firstName.trim() &&
    lastName.trim() &&
    !isLoading &&
    isPhoneOkay &&
    !getCorrectedPhoneNumberError
  );

  const errorMessage = !isLoading && isCorrectedPhoneNumberValid === false && (
    <Phrase id="CONTACT_INFO.ERROR" />
  );

  const warningMessage = !isLoading &&
    isMobile === false &&
    isCorrectedPhoneNumberValid && <Phrase id="CONTACT_INFO.WARNING.HTML" />;

  const handleFirstNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setFirstName(event.currentTarget.value));
  };

  const handleLastNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setLastName(event.currentTarget.value));
  };

  const handlePhoneChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = getPhoneNumberDigits(event.currentTarget.value);

    dispatch(setContactInfoPhoneInputValue(value));
  };

  useEffect(() => {
    // If the contact info component mounts, the customer location phone is
    // no longer valid, so we clear it out
    dispatch(setCustomerLocationPhone({}));
  }, [dispatch]);

  return (
    <FormGroup>
      <Container>
        <TextBox
          autoComplete="given-name"
          label={Phrase({ id: 'CONTACT_INFO.FIRST_NAME.LABEL' })}
          leftIcon={InputPersonSvg}
          name="fname"
          onChange={handleFirstNameChange}
          value={firstName}
        />
        <TextBox
          autoComplete="family-name"
          label={Phrase({ id: 'CONTACT_INFO.LAST_NAME.LABEL' })}
          leftIcon={InputPersonSvg}
          name="lname"
          onChange={handleLastNameChange}
          value={lastName}
        />
        <TextBox
          autoComplete="tel"
          cleaveOptions={{ phone: true, phoneRegionCode: countryCode }}
          description={Phrase({ id: 'CONTACT_INFO.PHONE.DESCRIPTION' })}
          errorMessage={errorMessage}
          isLoading={isLoading}
          isSuccessful={isPhoneSuccessful}
          label={Phrase({ id: 'CONTACT_INFO.PHONE.LABEL' })}
          leftIcon={InputMobilePhoneSvg}
          name="phone"
          onChange={handlePhoneChange}
          type="tel"
          value={correctedPhoneNumber}
          warningMessage={warningMessage}
        />
        {theme.contactInfoDisclaimerEnabled ? (
          <NoteContainer>
            <Phrase id="CONTACT_INFO.DISCLAIMER" />
          </NoteContainer>
        ) : null}
      </Container>
      <Buttons>
        <CarouselContinueButton
          destinationItemId={CarouselItemId.ServiceAreaSales}
          isDisabled={!isFormValid}
        />
      </Buttons>
    </FormGroup>
  );
};
